/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { getPositionFromStop, getStopFromMouseLocation, isColorInvalid, isStopInvalid } from './utils';
import { useMouseMove, getChromaColor } from '../utils';
import { keyCodes } from '../../../services';
import { EuiButtonIcon } from '../../button';
import { EuiColorPicker } from '../color_picker';
import { EuiFlexGroup, EuiFlexItem } from '../../flex';
import { EuiFieldNumber, EuiFormRow } from '../../form';
import { EuiI18n } from '../../i18n';
import { EuiPopover } from '../../popover';
import { EuiScreenReaderOnly } from '../../accessibility';
import { EuiSpacer } from '../../spacer';
import { EuiRangeThumb } from '../../form/range/range_thumb';
export var EuiColorStopThumb = ({
  className,
  stop,
  color,
  onChange,
  onFocus,
  onRemove,
  globalMin,
  globalMax,
  localMin,
  localMax,
  min,
  max,
  isRangeMin = false,
  isRangeMax = false,
  parentRef,
  colorPickerMode,
  colorPickerShowAlpha,
  colorPickerSwatches,
  disabled,
  readOnly,
  isPopoverOpen,
  openPopover,
  closePopover,
  'data-index': dataIndex,
  'aria-valuetext': ariaValueText
}) => {
  const background = useMemo(() => {
    const chromaColor = getChromaColor(color, colorPickerShowAlpha);
    return chromaColor ? chromaColor.css() : undefined;
  }, [color, colorPickerShowAlpha]);
  const [hasFocus, setHasFocus] = useState(isPopoverOpen);
  const [colorIsInvalid, setColorIsInvalid] = useState(isColorInvalid(color, colorPickerShowAlpha));
  const [stopIsInvalid, setStopIsInvalid] = useState(isStopInvalid(stop));
  const [numberInputRef, setNumberInputRef] = useState();
  const popoverRef = useRef(null);
  useEffect(() => {
    if (isPopoverOpen && popoverRef && popoverRef.current) {
      popoverRef.current.positionPopoverFixed();
    }
  }, [isPopoverOpen, stop]);

  const getStopFromMouseLocationFn = location => {
    // Guard against `null` ref in usage
    return getStopFromMouseLocation(location, parentRef, globalMin, globalMax);
  };

  const getPositionFromStopFn = stop => {
    // Guard against `null` ref in usage
    return getPositionFromStop(stop, parentRef, globalMin, globalMax);
  };

  const handleOnRemove = () => {
    if (onRemove) {
      closePopover();
      onRemove();
    }
  };

  const handleFocus = () => {
    setHasFocus(true);

    if (onFocus) {
      onFocus();
    }
  };

  const setHasFocusTrue = () => setHasFocus(true);

  const setHasFocusFalse = () => setHasFocus(false);

  const handleColorChange = value => {
    setColorIsInvalid(isColorInvalid(value, colorPickerShowAlpha));
    onChange({
      stop,
      color: value
    });
  };

  const handleStopChange = value => {
    const willBeInvalid = value > localMax || value < localMin;

    if (willBeInvalid) {
      if (value > localMax) {
        value = localMax;
      }

      if (value < localMin) {
        value = localMin;
      }
    }

    setStopIsInvalid(isStopInvalid(value));
    onChange({
      stop: value,
      color
    });
  };

  const handleStopInputChange = e => {
    let value = parseFloat(e.target.value);
    const willBeInvalid = value > globalMax || value < globalMin;

    if (willBeInvalid) {
      if (value > globalMax && max != null) {
        value = globalMax;
      }

      if (value < globalMin && min != null) {
        value = globalMin;
      }
    }

    setStopIsInvalid(isStopInvalid(value));
    onChange({
      stop: value,
      color
    });
  };

  const handlePointerChange = (location, isFirstInteraction) => {
    if (isFirstInteraction) return; // Prevents change on the inital MouseDown event

    if (parentRef == null) {
      return;
    }

    const newStop = getStopFromMouseLocationFn(location);
    handleStopChange(newStop);
  };

  const handleKeyDown = e => {
    switch (e.keyCode) {
      case keyCodes.ENTER:
        e.preventDefault();
        openPopover();
        break;

      case keyCodes.LEFT:
        e.preventDefault();
        if (readOnly) return;
        handleStopChange(stop - 1);
        break;

      case keyCodes.RIGHT:
        e.preventDefault();
        if (readOnly) return;
        handleStopChange(stop + 1);
        break;
    }
  };

  const [handleMouseDown, handleInteraction] = useMouseMove(handlePointerChange);

  const handleOnMouseDown = e => {
    if (!readOnly) {
      handleMouseDown(e);
    }

    openPopover();
  };

  const handleTouchInteraction = e => {
    if (!readOnly) {
      handleInteraction(e);
    }
  };

  const handleTouchStart = e => {
    handleTouchInteraction(e);
    openPopover();
  };

  const classes = classNames('euiColorStopPopover', {
    'euiColorStopPopover-hasFocus': hasFocus || isPopoverOpen
  }, className); // console.log('render', stop);

  return <EuiPopover ref={popoverRef} className={classes} anchorClassName="euiColorStopPopover__anchor" panelPaddingSize="s" isOpen={isPopoverOpen} closePopover={closePopover} ownFocus={isPopoverOpen} initialFocus={numberInputRef} panelClassName={numberInputRef ? undefined : 'euiColorStopPopover-isLoadingPanel'} style={{
    left: `${getPositionFromStopFn(stop)}%`
  }} button={<EuiI18n tokens={['euiColorStopThumb.buttonAriaLabel', 'euiColorStopThumb.buttonTitle']} defaults={['Press the Enter key to modify this stop. Press Escape to focus the group', 'Click to edit, drag to reposition']}>
          {([buttonAriaLabel, buttonTitle]) => {
      const ariaLabel = buttonAriaLabel;
      const title = buttonTitle;
      return <EuiRangeThumb data-test-subj="euiColorStopThumb" data-index={dataIndex} min={localMin} max={localMax} value={stop} onFocus={handleFocus} onBlur={setHasFocusFalse} onMouseOver={setHasFocusTrue} onMouseOut={setHasFocusFalse} onKeyDown={handleKeyDown} onMouseDown={handleOnMouseDown} onTouchStart={handleTouchStart} onTouchMove={handleTouchInteraction} aria-valuetext={ariaValueText} aria-label={ariaLabel} title={title} className="euiColorStopThumb" tabIndex={-1} style={{
        background
      }} disabled={disabled} />;
    }}
        </EuiI18n>}>
      <div className="euiColorStop" data-test-subj="euiColorStopPopover">
        <EuiScreenReaderOnly>
          <p aria-live="polite">
            <EuiI18n token="euiColorStopThumb.screenReaderAnnouncement" default="A popup with a color stop edit form opened.
            Tab forward to cycle through form controls or press
            escape to close this popup." />
          </p>
        </EuiScreenReaderOnly>
        <EuiFlexGroup gutterSize="s" responsive={false}>
          <EuiFlexItem>
            <EuiI18n tokens={['euiColorStopThumb.stopLabel', 'euiColorStopThumb.stopErrorMessage']} defaults={['Stop value', 'Value is out of range']}>
              {([stopLabel, stopErrorMessage]) => <EuiFormRow label={stopLabel} display="rowCompressed" isInvalid={stopIsInvalid} error={stopIsInvalid ? stopErrorMessage : null}>
                  <EuiFieldNumber inputRef={setNumberInputRef} compressed={true} readOnly={readOnly} min={isRangeMin || min == null ? undefined : localMin} max={isRangeMax || max == null ? undefined : localMax} value={isStopInvalid(stop) ? '' : stop} isInvalid={stopIsInvalid} onChange={handleStopInputChange} />
                </EuiFormRow>}
            </EuiI18n>
          </EuiFlexItem>
          {!readOnly && <EuiFlexItem grow={false}>
              <EuiFormRow display="rowCompressed" hasEmptyLabelSpace={true}>
                <EuiI18n token="euiColorStopThumb.removeLabel" default="Remove this stop">
                  {removeLabel => <EuiButtonIcon iconType="trash" color="danger" aria-label={removeLabel} title={removeLabel} disabled={!onRemove} onClick={handleOnRemove} />}
                </EuiI18n>
              </EuiFormRow>
            </EuiFlexItem>}
        </EuiFlexGroup>
        {!readOnly && <EuiSpacer size="s" />}
        <EuiColorPicker readOnly={readOnly} onChange={handleColorChange} color={color} mode={readOnly ? 'secondaryInput' : colorPickerMode} swatches={colorPickerSwatches} display="inline" showAlpha={colorPickerShowAlpha} isInvalid={colorIsInvalid} secondaryInputDisplay={colorPickerMode === 'swatch' ? 'none' : 'bottom'} />
      </div>
    </EuiPopover>;
};
EuiColorStopThumb.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onRemove: PropTypes.func,
  globalMin: PropTypes.number.isRequired,
  globalMax: PropTypes.number.isRequired,
  localMin: PropTypes.number.isRequired,
  localMax: PropTypes.number.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  isRangeMin: PropTypes.bool,
  isRangeMax: PropTypes.bool,
  parentRef: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.oneOf([null])]),
  colorPickerMode: PropTypes.oneOf(["default", "swatch", "picker", "secondaryInput"]).isRequired,
  colorPickerShowAlpha: PropTypes.bool,
  colorPickerSwatches: PropTypes.arrayOf(PropTypes.string.isRequired),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  isPopoverOpen: PropTypes.bool.isRequired,
  openPopover: PropTypes.func.isRequired,
  closePopover: PropTypes.func.isRequired,
  "data-index": PropTypes.string,
  "aria-valuetext": PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  stop: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired
};

try {
  // @ts-ignore
  EuiColorStopThumb.displayName = "EuiColorStopThumb"; // @ts-ignore

  EuiColorStopThumb.__docgenInfo = {
    "description": "",
    "displayName": "EuiColorStopThumb",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "onChange": {
        "defaultValue": null,
        "description": "",
        "name": "onChange",
        "required": true,
        "type": {
          "name": "(colorStop: ColorStop) => void"
        }
      },
      "onFocus": {
        "defaultValue": null,
        "description": "",
        "name": "onFocus",
        "required": false,
        "type": {
          "name": "(() => void) | undefined"
        }
      },
      "onRemove": {
        "defaultValue": null,
        "description": "",
        "name": "onRemove",
        "required": false,
        "type": {
          "name": "(() => void) | undefined"
        }
      },
      "globalMin": {
        "defaultValue": null,
        "description": "",
        "name": "globalMin",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "globalMax": {
        "defaultValue": null,
        "description": "",
        "name": "globalMax",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "localMin": {
        "defaultValue": null,
        "description": "",
        "name": "localMin",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "localMax": {
        "defaultValue": null,
        "description": "",
        "name": "localMax",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "min": {
        "defaultValue": null,
        "description": "",
        "name": "min",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "max": {
        "defaultValue": null,
        "description": "",
        "name": "max",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "isRangeMin": {
        "defaultValue": {
          value: false
        },
        "description": "",
        "name": "isRangeMin",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "isRangeMax": {
        "defaultValue": {
          value: false
        },
        "description": "",
        "name": "isRangeMax",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "parentRef": {
        "defaultValue": null,
        "description": "",
        "name": "parentRef",
        "required": false,
        "type": {
          "name": "HTMLDivElement | null | undefined"
        }
      },
      "colorPickerMode": {
        "defaultValue": null,
        "description": "",
        "name": "colorPickerMode",
        "required": true,
        "type": {
          "name": "\"default\" | \"swatch\" | \"picker\" | \"secondaryInput\" | undefined"
        }
      },
      "colorPickerShowAlpha": {
        "defaultValue": null,
        "description": "",
        "name": "colorPickerShowAlpha",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "colorPickerSwatches": {
        "defaultValue": null,
        "description": "",
        "name": "colorPickerSwatches",
        "required": false,
        "type": {
          "name": "string[] | undefined"
        }
      },
      "disabled": {
        "defaultValue": null,
        "description": "",
        "name": "disabled",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "readOnly": {
        "defaultValue": null,
        "description": "",
        "name": "readOnly",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "isPopoverOpen": {
        "defaultValue": null,
        "description": "",
        "name": "isPopoverOpen",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "openPopover": {
        "defaultValue": null,
        "description": "",
        "name": "openPopover",
        "required": true,
        "type": {
          "name": "() => void"
        }
      },
      "closePopover": {
        "defaultValue": null,
        "description": "",
        "name": "closePopover",
        "required": true,
        "type": {
          "name": "() => void"
        }
      },
      "data-index": {
        "defaultValue": null,
        "description": "",
        "name": "data-index",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-valuetext": {
        "defaultValue": null,
        "description": "",
        "name": "aria-valuetext",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "stop": {
        "defaultValue": null,
        "description": "",
        "name": "stop",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "color": {
        "defaultValue": null,
        "description": "",
        "name": "color",
        "required": true,
        "type": {
          "name": "string"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/color_picker/color_stops/color_stop_thumb.tsx#EuiColorStopThumb"] = {
      docgenInfo: EuiColorStopThumb.__docgenInfo,
      name: "EuiColorStopThumb",
      path: "src/components/color_picker/color_stops/color_stop_thumb.tsx#EuiColorStopThumb"
    };
} catch (__react_docgen_typescript_loader_error) {}